import React from 'react';
import CMSCustomSections from '../components/CMSCustomSections';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, ArrowRight, Info } from 'lucide-react';
import { useCMS } from '../hooks/useCMS';

const Pricing = () => {
  const navigate = useNavigate();
  const { content } = useCMS('pricing');

  const drivenServices = [
    {
      service: 'Concierge Black Car Service',
      description: 'Professional chauffeur in luxury sedan',
      retailPrice: '$150/hr',
      memberPrice: 'From $90/hr',
      savings: '40%'
    },
    {
      service: 'Wedding Transportation (Tier I)',
      description: 'Mercedes-Maybach GLS or S-Class',
      retailPrice: '$850 - $1,200',
      memberPrice: 'From $510 - $720',
      savings: '40%'
    },
    {
      service: 'Wedding Transportation (Tier II)',
      description: 'Rolls-Royce Ghost',
      retailPrice: '$1,800 - $2,200',
      memberPrice: 'From $1,080 - $1,320',
      savings: '40%'
    },
    {
      service: 'Airport Transfer',
      description: 'One-way luxury airport service',
      retailPrice: '$175 - $350',
      memberPrice: 'From $105 - $210',
      savings: '40%'
    },
    {
      service: 'Corporate Event (Full Day)',
      description: 'Executive transportation & coordination',
      retailPrice: '$2,000+',
      memberPrice: 'From $1,200+',
      savings: '40%'
    },
    {
      service: 'Luxury Sprinter/Coach',
      description: 'Group transportation with driver',
      retailPrice: '$400/hr',
      memberPrice: 'From $240/hr',
      savings: '40%'
    }
  ];

  const drivingServices = [
    {
      service: 'AuctionWell™ Car Buying',
      description: 'Full-service vehicle acquisition',
      retailPrice: '$500 + 3% of purchase',
      memberPrice: '$300 + 2% of purchase',
      savings: 'Significant'
    },
    {
      service: 'Automotive Consultation',
      description: 'Expert 1-on-1 advisory session',
      retailPrice: '$250/hr',
      memberPrice: 'From $150/hr',
      savings: '40%'
    },
    {
      service: 'Fleet Management',
      description: 'Multi-vehicle oversight & coordination',
      retailPrice: '$500/mo per vehicle',
      memberPrice: 'From $300/mo per vehicle',
      savings: '40%'
    },
    {
      service: 'Financing Consultation',
      description: 'Credit strategy & lender connections',
      retailPrice: '$350',
      memberPrice: 'From $210',
      savings: '40%'
    },
    {
      service: 'Vehicle Storage',
      description: 'Secure climate-controlled facility',
      retailPrice: '$400/mo',
      memberPrice: 'Included with Steel+',
      savings: '100%'
    },
    {
      service: 'Maintenance Coordination',
      description: 'Service scheduling & vendor management',
      retailPrice: '$150/service',
      memberPrice: 'From $90/service',
      savings: '40%'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold mb-6">{content('hero.title', 'Transparent Pricing')}</h1>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
            {content('hero.subtitle', 'No hidden fees. Clear pricing for every service.')}
          </p>
        </div>
      </div>

      {/* Notice */}
      <div className="bg-red-50 border-b border-red-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-3 text-red-800">
            <Info className="h-5 w-5" />
            <p className="text-sm">
              <strong>ACE Members save up to 40%</strong> on all services. 
              <button 
                onClick={() => navigate('/membership')}
                className="underline ml-1 hover:text-red-600"
              >
                View membership options
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Driven Services Pricing */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-black mb-4">Being DRIVEN Services</h2>
          <p className="text-xl text-gray-600">Professional chauffeur and transportation services</p>
        </div>

        <Card className="overflow-hidden border-gray-200 mb-16">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 text-black font-bold">Service</th>
                  <th className="text-right py-4 px-6 text-black font-bold">Retail Price</th>
                  <th className="text-right py-4 px-6 text-red-600 font-bold">Member Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drivenServices.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="font-semibold text-black">{item.service}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </td>
                    <td className="text-right py-4 px-6 text-gray-600">{item.retailPrice}</td>
                    <td className="text-right py-4 px-6">
                      <span className="text-red-600 font-semibold">{item.memberPrice}</span>
                      <span className="text-xs text-green-600 ml-2">Save {item.savings}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Driving Services Pricing */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-black mb-4">DRIVING Services</h2>
          <p className="text-xl text-gray-600">Vehicle acquisition, fleet management, and concierge services</p>
        </div>

        <Card className="overflow-hidden border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 text-black font-bold">Service</th>
                  <th className="text-right py-4 px-6 text-black font-bold">Retail Price</th>
                  <th className="text-right py-4 px-6 text-red-600 font-bold">Member Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drivingServices.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="font-semibold text-black">{item.service}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </td>
                    <td className="text-right py-4 px-6 text-gray-600">{item.retailPrice}</td>
                    <td className="text-right py-4 px-6">
                      <span className="text-red-600 font-semibold">{item.memberPrice}</span>
                      <span className="text-xs text-green-600 ml-2">Save {item.savings}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Custom Quote Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Need a Custom Quote?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border-gray-200">
              <h3 className="text-xl font-bold text-black mb-4">Multi-Event Package</h3>
              <p className="text-gray-600 mb-6">Planning multiple events or need ongoing service? We offer custom packages with additional discounts.</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Volume discounts
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Dedicated coordinator
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Flexible scheduling
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/contact')}
                className="w-full bg-black hover:bg-gray-900 text-white"
              >
                Get Quote
              </Button>
            </Card>

            <Card className="p-8 border-gray-200">
              <h3 className="text-xl font-bold text-black mb-4">Corporate Account</h3>
              <p className="text-gray-600 mb-6">Businesses with regular transportation or fleet needs benefit from corporate pricing.</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Monthly invoicing
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Priority booking
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Usage reporting
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/corporate')}
                className="w-full bg-black hover:bg-gray-900 text-white"
              >
                Corporate Info
              </Button>
            </Card>

            <Card className="p-8 border-gray-200">
              <h3 className="text-xl font-bold text-black mb-4">Wedding Planner</h3>
              <p className="text-gray-600 mb-6">Wedding planners and event coordinators receive special partnership rates.</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Referral commissions
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Co-branded materials
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-red-600 mr-2" />
                  Direct booking line
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/partners')}
                className="w-full bg-black hover:bg-gray-900 text-white"
              >
                Partner With Us
              </Button>
            </Card>
          </div>
        </div>
      </div>

      {/* Membership CTA */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Unlock Member Pricing</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join the Auto Concierge♠Club and save up to 40% on all services
          </p>
          <Button 
            onClick={() => navigate('/membership')}
            size="lg"
            className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg"
          >
            View Membership Options
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
      <CMSCustomSections pageId="pricing" dark />
    </div>
  );
};

export default Pricing;
